/**
 * CaribbeanLeaf Route Configuration
 *
 * Lazy-loaded routes with protected route wrapper for cannabis POS.
 * All routes except login require authentication.
 */

import { lazy, Suspense, ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';

// Lazy-loaded pages for code splitting
const Login = lazy(() => import('@/pages/cannabis/Login'));
const Dashboard = lazy(() => import('@/pages/cannabis/Dashboard'));
const POSTerminal = lazy(() => import('@/pages/cannabis/POSTerminal'));
const CheckoutPage = lazy(() => import('@/pages/cannabis/CheckoutPage'));
const Patients = lazy(() => import('@/pages/cannabis/Patients'));
const Inventory = lazy(() => import('@/pages/cannabis/Inventory'));
const Compliance = lazy(() => import('@/pages/cannabis/Compliance'));
const Settings = lazy(() => import('@/pages/settings/SettingsPage'));
const StaffOrders = lazy(() => import('@/pages/cannabis/StaffOrdersPage'));
const BatchList = lazy(() => import('@/pages/cannabis/BatchListPage'));
const PatientDetail = lazy(() => import('@/pages/cannabis/PatientDetailPage'));
const TransactionHistory = lazy(() => import('@/pages/cannabis/TransactionHistoryPage'));
const NotificationPreferences = lazy(() => import('@/pages/settings/NotificationPreferencesPage'));

// Admin pages
const AdminUsers = lazy(() => import('@/pages/admin/UserListPage'));
const AdminLocations = lazy(() => import('@/pages/admin/LocationsPage'));
const AdminSettings = lazy(() => import('@/pages/admin/SettingsPage'));
const SacramentalQueue = lazy(() => import('@/pages/admin/SacramentalQueuePage'));
const SacramentalReview = lazy(() => import('@/pages/admin/SacramentalReviewPage'));
const SacramentalAuthorizations = lazy(() => import('@/pages/admin/SacramentalAuthorizationsPage'));

/**
 * Loading fallback for lazy-loaded pages
 */
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="space-y-4 w-full max-w-md p-8">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );
}

/**
 * Suspense wrapper for lazy pages
 */
function LazyPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/**
 * Protected route wrapper - redirects to login if not authenticated
 */
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

/**
 * Public route wrapper - redirects to dashboard if already authenticated
 */
function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

/**
 * Route definitions for CaribbeanLeaf Cannabis POS
 *
 * Structure:
 * - /login (public)
 * - / (protected - dashboard)
 * - /pos (protected - POS terminal)
 * - /patients (protected - patient registry)
 * - /inventory (protected - inventory management)
 * - /compliance (protected - compliance reports)
 * - /settings (protected - user settings)
 */
export const routes = [
  // Public routes (login)
  {
    element: <PublicRoute />,
    children: [
      {
        path: '/login',
        element: (
          <LazyPage>
            <Login />
          </LazyPage>
        ),
      },
    ],
  },
  // Protected routes (require authentication)
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/',
        element: (
          <LazyPage>
            <Dashboard />
          </LazyPage>
        ),
      },
      {
        path: '/pos',
        element: (
          <LazyPage>
            <POSTerminal />
          </LazyPage>
        ),
      },
      {
        path: '/pos/checkout',
        element: (
          <LazyPage>
            <CheckoutPage />
          </LazyPage>
        ),
      },
      {
        path: '/patients',
        element: (
          <LazyPage>
            <Patients />
          </LazyPage>
        ),
      },
      {
        path: '/inventory',
        element: (
          <LazyPage>
            <Inventory />
          </LazyPage>
        ),
      },
      {
        path: '/pos/transactions',
        element: (
          <LazyPage>
            <TransactionHistory />
          </LazyPage>
        ),
      },
      {
        path: '/patients/:id',
        element: (
          <LazyPage>
            <PatientDetail />
          </LazyPage>
        ),
      },
      {
        path: '/inventory/batches',
        element: (
          <LazyPage>
            <BatchList />
          </LazyPage>
        ),
      },
      {
        path: '/staff/orders',
        element: (
          <LazyPage>
            <StaffOrders />
          </LazyPage>
        ),
      },
      {
        path: '/compliance',
        element: (
          <LazyPage>
            <Compliance />
          </LazyPage>
        ),
      },
      {
        path: '/settings',
        element: (
          <LazyPage>
            <Settings />
          </LazyPage>
        ),
      },
      {
        path: '/settings/notifications',
        element: (
          <LazyPage>
            <NotificationPreferences />
          </LazyPage>
        ),
      },
      // Admin routes
      {
        path: '/admin/users',
        element: (
          <LazyPage>
            <AdminUsers />
          </LazyPage>
        ),
      },
      {
        path: '/admin/locations',
        element: (
          <LazyPage>
            <AdminLocations />
          </LazyPage>
        ),
      },
      {
        path: '/admin/settings',
        element: (
          <LazyPage>
            <AdminSettings />
          </LazyPage>
        ),
      },
      {
        path: '/admin/sacramental',
        element: (
          <LazyPage>
            <SacramentalQueue />
          </LazyPage>
        ),
      },
      {
        path: '/admin/sacramental/all',
        element: (
          <LazyPage>
            <SacramentalAuthorizations />
          </LazyPage>
        ),
      },
      {
        path: '/admin/sacramental/:id',
        element: (
          <LazyPage>
            <SacramentalReview />
          </LazyPage>
        ),
      },
    ],
  },
  // Catch-all redirect to dashboard
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

export default routes;
